import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnInit, Output, Input, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DialogBaseComponent } from '@/app/core/components';
import { Button } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { DropdownModule } from 'primeng/dropdown';
import { TextareaModule } from 'primeng/textarea';
import { CalendarModule } from 'primeng/calendar';
import { BatchRecordsResponseDto } from '@/app/core/services/batch-records.service';
import { parseDateOnly } from '@/app/shared/utils';

export interface BatchRecordData {
    id?: string;
    batchNumber?: string;
    manufactureDate?: Date;
    expiryDate?: Date;
    quantity?: string;
    status?: string;
    releasedDate?: Date;
    releaseNotes?: string;
    qcOfficerId?: string;
}

@Component({
    selector: 'qc-add-batch-record-dialog',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        Button,
        InputTextModule,
        InputNumberModule,
        DropdownModule,
        TextareaModule,
        CalendarModule,
    ],
    template: `
        <div class="w-[600px] max-w-[90vw] bg-white rounded-lg shadow-2xl" style="overflow: visible;">
            <div class="flex justify-between items-center p-6 border-b border-gray-200">
                <h2 class="text-xl font-semibold text-gray-900">
                    {{ isEditMode ? 'Edit Batch Record' : 'Batch Record' }}
                </h2>
                <button type="button" class="p-2 hover:bg-gray-100 rounded-lg" (click)="close()">
                    <i class="pi pi-times text-gray-400"></i>
                </button>
            </div>

            <div class="p-6 max-h-[70vh] overflow-y-visible">
                <form [formGroup]="batchRecordForm" class="space-y-4">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div class="form-field">
                            <label class="block text-sm font-medium text-gray-700 mb-1">Batch Number *</label>
                            <input
                                pInputText
                                formControlName="batchNumber"
                                placeholder="Enter batch number"
                                class="w-full"
                            />
                        </div>

                        <div class="form-field">
                            <label class="block text-sm font-medium text-gray-700 mb-1">Manufacture Date</label>
                            <p-calendar
                                formControlName="manufactureDate"
                                dateFormat="yy-mm-dd"
                                [showIcon]="true"
                                styleClass="w-full"
                            />
                        </div>

                        <div class="form-field">
                            <label class="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
                            <p-calendar
                                formControlName="expiryDate"
                                dateFormat="yy-mm-dd"
                                [showIcon]="true"
                                styleClass="w-full"
                            />
                        </div>

                        <div class="form-field">
                            <label class="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                            <input
                                pInputText
                                formControlName="quantity"
                                placeholder="Enter quantity (e.g., 800 kg)"
                                class="w-full"
                            />
                        </div>

                        <div class="form-field">
                            <label class="block text-sm font-medium text-gray-700 mb-1">Status</label>
                            <p-dropdown
                                formControlName="status"
                                [options]="statusOptions"
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Select status"
                                [showClear]="true"
                                styleClass="w-full"
                            />
                        </div>

                        <div class="form-field">
                            <label class="block text-sm font-medium text-gray-700 mb-1">Released Date</label>
                            <p-calendar
                                formControlName="releasedDate"
                                dateFormat="yy-mm-dd"
                                [showIcon]="true"
                                styleClass="w-full"
                            />
                        </div>

                        <div class="form-field md:col-span-2">
                            <label class="block text-sm font-medium text-gray-700 mb-1">Release Notes</label>
                            <textarea
                                pInputTextarea
                                formControlName="releaseNotes"
                                placeholder="Enter release notes"
                                rows="3"
                                class="w-full"
                            ></textarea>
                        </div>

                        <div class="form-field">
                            <label class="block text-sm font-medium text-gray-700 mb-1">QC Officer ID</label>
                            <input
                                pInputText
                                formControlName="qcOfficerId"
                                placeholder="Enter QC Officer ID"
                                class="w-full"
                            />
                        </div>
                    </div>
                </form>
            </div>

            <div class="flex justify-end gap-3 p-6 border-t border-gray-200">
                <p-button severity="secondary" variant="outlined" (click)="close()" label="Cancel" />
                <p-button severity="primary" (click)="save()" [label]="isEditMode ? 'Update' : 'Save'" />
            </div>
        </div>
    `,
    styles: [
        `
            :host ::ng-deep .p-calendar {
                z-index: 9999;
            }
            :host ::ng-deep .p-dropdown-panel {
                z-index: 9999;
            }
            :host ::ng-deep .p-calendar-panel {
                position: absolute !important;
                z-index: 10000 !important;
                top: auto !important;
                left: auto !important;
            }
        `,
    ],
})
export class AddBatchRecordDialogComponent extends DialogBaseComponent implements OnInit {
    constructor() {
        super();
    }
    @Input() initialData: BatchRecordsResponseDto | null = null;
    @Output() batchRecordAdded = new EventEmitter<BatchRecordData>();

    private fb = inject(FormBuilder);

    batchRecordForm!: FormGroup;

    statusOptions = [
        { label: 'Released', value: 'Released' },
        { label: 'Pending', value: 'Pending' },
        { label: 'Rejected', value: 'Rejected' },
        { label: 'Hold', value: 'Hold' },
    ];

    get isEditMode(): boolean {
        return !!this.initialData?.id;
    }

    ngOnInit() {
        this.initForm();
        if (this.initialData) {
            this.loadInitialData();
        }
    }

    private initForm() {
        this.batchRecordForm = this.fb.group({
            batchNumber: [''],
            manufactureDate: [''],
            expiryDate: [''],
            quantity: [''],
            status: [''],
            releasedDate: [''],
            releaseNotes: [''],
            qcOfficerId: [''],
        });
    }

    private loadInitialData() {
        if (this.initialData) {
            this.batchRecordForm.patchValue({
                ...this.initialData,
                manufactureDate: parseDateOnly(this.initialData.manufactureDate) ?? undefined,
                expiryDate: parseDateOnly(this.initialData.expiryDate) ?? undefined,
                releasedDate: parseDateOnly(this.initialData.releasedDate) ?? undefined,
            });
        }
    }

    save() {
        if (this.batchRecordForm.valid) {
            const formValue = this.batchRecordForm.value;

            const processedData: BatchRecordData = {
                id: this.initialData?.id,
                batchNumber: formValue.batchNumber || undefined,
                manufactureDate: formValue.manufactureDate || undefined,
                expiryDate: formValue.expiryDate || undefined,
                quantity: formValue.quantity || undefined,
                status: formValue.status || undefined,
                releasedDate: formValue.releasedDate || undefined,
                releaseNotes: formValue.releaseNotes || undefined,
                qcOfficerId: formValue.qcOfficerId || undefined,
            };

            this.batchRecordAdded.emit(processedData);
            this.close();
        }
    }
}
